import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { useFeedback } from '@/components/feedback/feedback-context'
import { CopyableReadonlyField } from '@/components/forms/copyable-readonly-field'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Select } from '@/components/forms/select'
import { Textarea } from '@/components/forms/textarea'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { Drawer } from '@/components/overlays/drawer'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { PageHeader } from '@/components/layout/page-header'
import { Breadcrumbs } from '@/components/navigation/breadcrumbs'
import { ConfirmationDialog } from '@/components/overlays/confirmation-dialog'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { cn } from '@/lib/cn'

import {
  getOidcClient,
  getOidcProviderConfiguration,
  listOAuthResources,
  listOidcApplicationOptions,
  listOidcClientScopes,
  rotateOidcClientSecret,
  updateOidcClient,
  type OidcClient,
  type OidcClientCreated,
  type OidcClientPayload,
  type OidcClientScopeAssignmentType,
} from './api'
import { OidcClientCredentialsModal } from './idp-client-credentials-modal'
import { IdpClientTabs, type IdpClientDetailTab } from './idp-client-tabs'
import { isOidcIdpReadOnly } from './idp-permissions'

function toPayload(client: OidcClient): OidcClientPayload {
  return {
    application_ids: client.application_ids,
    client_kind: client.client_kind,
    default_client_scope_ids: client.default_client_scope_ids,
    name: client.name,
    optional_client_scope_ids: client.optional_client_scope_ids,
    redirect_uris: client.redirect_uris,
    resource_id: client.resource_id,
    status: client.status,
    token_endpoint_auth_method: client.token_endpoint_auth_method,
  }
}

function editLines(value: string) {
  return value.split('\n')
}

function normalizeLines(values: string[]) {
  return values.map((item) => item.trim()).filter(Boolean)
}

function FieldHelp({ text }: { text: string }) {
  return (
    <HelpTooltip label={text} widthClassName="w-[280px]">
      {text}
    </HelpTooltip>
  )
}

function OidcClientDetailWorkspace({
  clientRecordId,
  onBack,
  onOpenChange,
  open = true,
  presentation,
}: {
  clientRecordId: string
  onBack?: () => void
  onOpenChange?: (open: boolean) => void
  open?: boolean
  presentation: 'drawer' | 'page'
}) {
  const { t } = useTranslation('adminIdp')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const readOnly = isOidcIdpReadOnly()
  const [tab, setTab] = useState<IdpClientDetailTab>('settings')
  const [draft, setDraft] = useState<{ id: string; values: OidcClientPayload } | null>(null)
  const [rotatedClient, setRotatedClient] = useState<OidcClientCreated | null>(null)
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false)
  const detailQueryKey = ['admin-idp-client', clientRecordId]
  const detailQuery = useQuery({
    enabled: Boolean(clientRecordId),
    queryFn: () => getOidcClient(clientRecordId),
    queryKey: detailQueryKey,
  })
  const providerQuery = useQuery({
    queryFn: getOidcProviderConfiguration,
    queryKey: ['admin-idp-provider'],
  })
  const scopesQuery = useQuery({
    queryFn: listOidcClientScopes,
    queryKey: ['admin-idp-client-scopes'],
  })
  const resourcesQuery = useQuery({
    queryFn: listOAuthResources,
    queryKey: ['admin-idp-oauth-resources'],
  })
  const applicationsQuery = useQuery({
    queryFn: listOidcApplicationOptions,
    queryKey: ['admin-idp-application-options'],
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: detailQueryKey })
    void queryClient.invalidateQueries({ queryKey: ['admin-idp-clients'] })
    void queryClient.invalidateQueries({ queryKey: ['admin-idp-application-options'] })
  }
  const updateMutation = useMutation({
    mutationFn: updateOidcClient,
    onError: () => showFeedback({ message: t('clients.errors.update'), tone: 'error' }),
    onSuccess: () => {
      setDraft(null)
      refresh()
      showFeedback({ message: t('clients.success.updated'), tone: 'success' })
    },
  })
  const rotateMutation = useMutation({
    mutationFn: rotateOidcClientSecret,
    onError: () => showFeedback({ message: t('clients.errors.rotateSecret'), tone: 'error' }),
    onSuccess: (client) => {
      setRotateConfirmOpen(false)
      setRotatedClient(client)
      refresh()
      showFeedback({ message: t('clients.success.secretRotated'), tone: 'success' })
    },
  })

  const client = detailQuery.data
  const values = client ? (draft?.id === clientRecordId ? draft.values : toPayload(client)) : null
  const updateValues = (patch: Partial<OidcClientPayload>) => {
    if (!client || !values) return
    setDraft({ id: clientRecordId, values: { ...values, ...patch } })
  }
  const updateScopeAssignment = (
    scopeId: string,
    assignment: OidcClientScopeAssignmentType | '',
  ) => {
    if (!values) return
    updateValues({
      default_client_scope_ids: [
        ...values.default_client_scope_ids.filter((id) => id !== scopeId),
        ...(assignment === 'default' ? [scopeId] : []),
      ],
      optional_client_scope_ids: [
        ...values.optional_client_scope_ids.filter((id) => id !== scopeId),
        ...(assignment === 'optional' ? [scopeId] : []),
      ],
    })
  }
  const save = () => {
    if (values) {
      updateMutation.mutate({
        clientRecordId,
        payload: {
          ...values,
          redirect_uris: normalizeLines(values.redirect_uris),
        },
      })
    }
  }
  const copy = (value: string) => void navigator.clipboard.writeText(value)
  const scopeNames = client
    ? [
        ...(client.client_kind === 'oidc_embed' ? ['openid'] : []),
        ...client.client_scope_assignments.map((scope) => scope.scope_name),
      ].join(' ')
    : ''
  const resourceOptions = (resourcesQuery.data ?? [])
    .filter((resource) => resource.status === 'active' || resource.id === values?.resource_id)
    .map((resource) => ({
      description: resource.resource_uri,
      label: resource.name,
      value: resource.id,
    }))
  const contentClassName =
    tab === 'settings' || tab === 'credentials' ? 'max-w-[640px]' : 'max-w-[920px]'

  const detailPanel = (
    <div
      aria-labelledby={'oidc-client-' + tab + '-tab'}
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain pt-6 pb-5',
        presentation === 'drawer' ? 'px-6' : 'px-5 lg:px-[30px]',
      )}
      id={'oidc-client-' + tab + '-panel'}
      role="tabpanel"
      tabIndex={0}
      data-testid="oidc-client-detail-scroll"
    >
      <PageContentBoundary className={contentClassName}>
        {detailQuery.isError ? (
          <p className="text-sm text-[var(--color-danger)]">{t('clients.errors.loadDetail')}</p>
        ) : detailQuery.isLoading || !client || !values ? (
          <div className="h-32 animate-pulse rounded-lg bg-[var(--color-surface-muted)]" />
        ) : tab === 'settings' ? (
          <div className="grid gap-5">
            <FormField
              htmlFor="oidc-client-name"
              label={t('clients.fields.name')}
              labelExtra={<FieldHelp text={t('clients.fields.nameHelp')} />}
              required
            >
              <Input
                disabled={readOnly}
                id="oidc-client-name"
                onChange={(event) => updateValues({ name: event.target.value })}
                value={values.name}
              />
            </FormField>
            <FormField htmlFor="oidc-client-kind" label={t('clients.fields.clientKind')}>
              <Input
                disabled
                id="oidc-client-kind"
                value={client.client_kind === 'mcp_oauth' ? 'MCP OAuth' : 'OpenID Connect'}
              />
            </FormField>
            {client.client_kind === 'mcp_oauth' ? (
              <FormField
                error={resourcesQuery.isError ? t('resources.errors.load') : undefined}
                htmlFor="oidc-client-resource"
                label={t('clients.fields.resource')}
                required
              >
                <Select
                  disabled={readOnly || resourcesQuery.isError || resourceOptions.length === 0}
                  id="oidc-client-resource"
                  onValueChange={(resourceId) => updateValues({ resource_id: resourceId })}
                  options={resourceOptions}
                  value={values.resource_id ?? ''}
                />
              </FormField>
            ) : null}
            <FormField
              htmlFor="oidc-client-callback-urls"
              label={t('clients.fields.callbackUrls')}
              labelExtra={<FieldHelp text={t('clients.fields.callbackUrlsHelp')} />}
            >
              <Textarea
                disabled={readOnly}
                id="oidc-client-callback-urls"
                onChange={(event) => updateValues({ redirect_uris: editLines(event.target.value) })}
                rows={5}
                value={values.redirect_uris.join('\n')}
              />
            </FormField>
            <div className="grid gap-5">
              <FormField
                htmlFor="oidc-client-auth-method"
                label={t('clients.fields.authMethod')}
                labelExtra={<FieldHelp text={t('clients.fields.authMethodHelp')} />}
              >
                <Select
                  disabled={readOnly}
                  id="oidc-client-auth-method"
                  onValueChange={(value) =>
                    updateValues({
                      token_endpoint_auth_method:
                        value as OidcClientPayload['token_endpoint_auth_method'],
                    })
                  }
                  options={[
                    { label: 'client_secret_basic', value: 'client_secret_basic' },
                    { label: 'client_secret_post', value: 'client_secret_post' },
                  ]}
                  value={values.token_endpoint_auth_method}
                />
              </FormField>
              <FormField
                htmlFor="oidc-client-status"
                label={t('clients.fields.status')}
                labelExtra={<FieldHelp text={t('clients.fields.statusHelp')} />}
              >
                <Select
                  disabled={readOnly}
                  id="oidc-client-status"
                  onValueChange={(value) =>
                    updateValues({ status: value as OidcClientPayload['status'] })
                  }
                  options={[
                    { label: t('status.enabled'), value: 'active' },
                    { label: t('status.disabled'), value: 'inactive' },
                  ]}
                  value={values.status}
                />
              </FormField>
            </div>
            {readOnly ? null : (
              <div>
                <Button
                  disabled={
                    !values.name.trim() ||
                    (client.client_kind === 'mcp_oauth' && !values.resource_id)
                  }
                  isLoading={updateMutation.isPending}
                  onClick={save}
                >
                  {t('actions.save')}
                </Button>
              </div>
            )}
          </div>
        ) : tab === 'credentials' ? (
          <div className="grid gap-6">
            <div className="grid gap-4">
              <CopyableReadonlyField
                copyLabel={t('actions.copy')}
                label={t('parameters.discoveryUrl')}
                monospace
                onCopy={() =>
                  copy(
                    (client.client_kind === 'mcp_oauth'
                      ? providerQuery.data?.oauth_discovery_url
                      : providerQuery.data?.discovery_url) ?? '',
                  )
                }
                showLabel
                value={
                  (client.client_kind === 'mcp_oauth'
                    ? providerQuery.data?.oauth_discovery_url
                    : providerQuery.data?.discovery_url) ?? ''
                }
              />
              {client.resource_uri ? (
                <CopyableReadonlyField
                  copyLabel={t('actions.copy')}
                  label={t('clients.fields.resource')}
                  monospace
                  onCopy={() => copy(client.resource_uri ?? '')}
                  showLabel
                  value={client.resource_uri}
                />
              ) : null}
              <CopyableReadonlyField
                copyLabel={t('actions.copy')}
                label={t('clients.fields.clientId')}
                monospace
                onCopy={() => copy(client.client_id)}
                showLabel
                value={client.client_id}
              />
              <CopyableReadonlyField
                copyLabel={t('actions.copy')}
                label={t('clients.fields.scope')}
                monospace
                onCopy={() => copy(scopeNames)}
                showLabel
                value={scopeNames}
              />
              <CopyableReadonlyField
                copyLabel={t('actions.copy')}
                label={t('clients.fields.authMethod')}
                monospace
                onCopy={() => copy(client.token_endpoint_auth_method)}
                showLabel
                value={client.token_endpoint_auth_method}
              />
            </div>
            {readOnly ? null : (
              <div className="border-t border-[var(--color-divider)] pt-5">
                <h2 className="text-sm font-semibold">{t('clients.credentials.rotateTitle')}</h2>
                <p className="mt-1 max-w-[680px] text-sm text-[var(--color-text-muted)]">
                  {t('clients.credentials.rotateHint')}
                </p>
                <Button
                  className="mt-4"
                  isLoading={rotateMutation.isPending}
                  onClick={() => setRotateConfirmOpen(true)}
                  variant="secondary"
                >
                  {t('clients.credentials.rotate')}
                </Button>
              </div>
            )}
          </div>
        ) : tab === 'clientScopes' ? (
          <div>
            <p className="mb-4 text-sm text-[var(--color-text-muted)]">
              {t('clients.clientScopesDescription')}
            </p>
            <div className="overflow-hidden rounded-lg border border-[var(--color-panel-border)]">
              {(scopesQuery.data ?? [])
                .filter((scope) => scope.enabled)
                .map((scope) => {
                  const assignment: OidcClientScopeAssignmentType | '' =
                    values.default_client_scope_ids.includes(scope.id)
                      ? 'default'
                      : values.optional_client_scope_ids.includes(scope.id)
                        ? 'optional'
                        : ''
                  return (
                    <div
                      className="grid items-center gap-4 border-b border-[var(--color-divider)] px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto]"
                      key={scope.id}
                    >
                      <label
                        className={cn(
                          'flex min-w-0 items-start gap-3',
                          readOnly ? 'cursor-not-allowed' : 'cursor-pointer',
                        )}
                      >
                        <input
                          aria-label={t('clients.fields.assignScope', { name: scope.display_name })}
                          checked={assignment !== ''}
                          className="mt-1 size-4 shrink-0 accent-[var(--color-primary)]"
                          disabled={readOnly}
                          onChange={(event) =>
                            updateScopeAssignment(scope.id, event.target.checked ? 'default' : '')
                          }
                          type="checkbox"
                        />
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{scope.display_name}</span>
                            <code className="text-xs text-[var(--color-text-muted)]">
                              {scope.name}
                            </code>
                            {scope.system ? (
                              <Badge variant="neutral">{t('clientScopes.builtIn')}</Badge>
                            ) : null}
                          </span>
                          {scope.description ? (
                            <span className="mt-1 block text-sm text-[var(--color-text-muted)]">
                              {scope.description}
                            </span>
                          ) : null}
                        </span>
                      </label>
                      <div
                        aria-label={t('clients.fields.scopeAssignment', {
                          name: scope.display_name,
                        })}
                        className="inline-flex w-fit rounded-lg bg-[var(--color-surface-muted)] p-1"
                        role="group"
                      >
                        {(['default', 'optional'] as const).map((nextAssignment) => (
                          <button
                            aria-pressed={assignment === nextAssignment}
                            className={cn(
                              'h-8 min-w-[84px] rounded-md px-3 text-sm font-medium transition-colors',
                              assignment === nextAssignment
                                ? 'bg-white text-[var(--color-primary)] shadow-[var(--shadow-control)]'
                                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
                              assignment === '' || readOnly
                                ? 'cursor-not-allowed opacity-55'
                                : 'cursor-pointer',
                            )}
                            disabled={assignment === '' || readOnly}
                            key={nextAssignment}
                            onClick={() => updateScopeAssignment(scope.id, nextAssignment)}
                            type="button"
                          >
                            {t(`clients.scopeAssignments.${nextAssignment}`)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
            </div>
            {readOnly ? null : (
              <Button className="mt-5" isLoading={updateMutation.isPending} onClick={save}>
                {t('actions.save')}
              </Button>
            )}
          </div>
        ) : (
          <div>
            <p className="mb-4 text-sm text-[var(--color-text-muted)]">
              {t('clients.applicationsDescription')}
            </p>
            <div className="overflow-hidden rounded-lg border border-[var(--color-panel-border)]">
              {(applicationsQuery.data ?? []).map((application) => {
                const boundElsewhere = Boolean(
                  application.bound_client_record_id &&
                  application.bound_client_record_id !== clientRecordId,
                )
                return (
                  <label
                    className={cn(
                      'flex items-center justify-between gap-4 border-b border-[var(--color-divider)] px-4 py-3 last:border-b-0',
                      boundElsewhere || readOnly
                        ? 'cursor-not-allowed bg-[var(--color-surface-muted)]'
                        : 'cursor-pointer',
                    )}
                    key={application.id}
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <input
                        checked={values.application_ids.includes(application.id)}
                        className="size-4 accent-[var(--color-primary)]"
                        disabled={boundElsewhere || readOnly}
                        onChange={(event) =>
                          updateValues({
                            application_ids: event.target.checked
                              ? [...values.application_ids, application.id]
                              : values.application_ids.filter((id) => id !== application.id),
                          })
                        }
                        type="checkbox"
                      />
                      <span>
                        <span className="block font-medium">{application.name}</span>
                        {boundElsewhere ? (
                          <span className="mt-0.5 block text-xs text-[var(--color-text-muted)]">
                            {t('clients.applicationBoundElsewhere')}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <Badge variant={application.status === 'published' ? 'success' : 'neutral'}>
                      {application.status}
                    </Badge>
                  </label>
                )
              })}
              {applicationsQuery.data?.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
                  {t('clients.noApplications')}
                </p>
              ) : null}
            </div>
            {readOnly ? null : (
              <Button className="mt-5" isLoading={updateMutation.isPending} onClick={save}>
                {t('actions.save')}
              </Button>
            )}
          </div>
        )}
      </PageContentBoundary>
    </div>
  )
  const credentialsModal = (
    <OidcClientCredentialsModal
      client={rotatedClient}
      onClose={() => setRotatedClient(null)}
      onRetryProvider={() => void providerQuery.refetch()}
      provider={providerQuery.data ?? null}
      providerError={providerQuery.isError}
      providerLoading={providerQuery.isFetching}
    />
  )
  const rotateDialog = (
    <ConfirmationDialog
      cancelLabel={t('actions.cancel')}
      confirmLabel={t('actions.rotateSecret')}
      description={t('clients.credentials.rotateConfirm')}
      isConfirming={rotateMutation.isPending}
      onCancel={() => setRotateConfirmOpen(false)}
      onConfirm={() => rotateMutation.mutate(clientRecordId)}
      open={rotateConfirmOpen}
      title={t('clients.credentials.rotateConfirmTitle')}
    />
  )

  if (presentation === 'drawer') {
    return (
      <>
        <Drawer
          bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
          headerAddon={
            <IdpClientTabs
              activeTab={tab}
              embedded
              onTabChange={setTab}
              showApplications={client?.client_kind !== 'mcp_oauth'}
            />
          }
          onOpenChange={onOpenChange ?? (() => undefined)}
          open={open}
          size="lg"
          title={t('clients.editTitle', {
            name: client?.name ?? t('clients.detailTitle'),
          })}
        >
          {detailPanel}
        </Drawer>
        {rotateDialog}
        {credentialsModal}
      </>
    )
  }

  return (
    <AdminShellPage activePath="/admin/idp/clients">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          breadcrumbs={
            <Breadcrumbs
              ariaLabel={t('breadcrumbs.label')}
              items={[
                {
                  href: '/admin/idp/clients',
                  label: t('clients.title'),
                  onNavigate: onBack,
                  title: t('actions.backToClients'),
                },
                {
                  label: client?.name ?? t('clients.detailTitle'),
                },
              ]}
              variant="pageHeader"
            />
          }
          className="shrink-0"
          contentClassName={contentClassName}
        />
        <IdpClientTabs
          activeTab={tab}
          contentClassName={contentClassName}
          onTabChange={setTab}
          showApplications={client?.client_kind !== 'mcp_oauth'}
        />
        {detailPanel}
      </div>
      {rotateDialog}
      {credentialsModal}
    </AdminShellPage>
  )
}

export function OidcClientDetailDrawer({
  clientRecordId,
  onOpenChange,
  open,
}: {
  clientRecordId: string
  onOpenChange: (open: boolean) => void
  open: boolean
}) {
  return (
    <OidcClientDetailWorkspace
      clientRecordId={clientRecordId}
      onOpenChange={onOpenChange}
      open={open}
      presentation="drawer"
    />
  )
}

export function AdminOidcClientDetailPage() {
  const params = useParams({ strict: false })
  const navigate = useNavigate()

  return (
    <OidcClientDetailWorkspace
      clientRecordId={params.clientRecordId ?? ''}
      onBack={() => void navigate({ to: '/admin/idp/clients' })}
      presentation="page"
    />
  )
}
